// components/TokenDetail.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Web3 from 'web3';
import { contractABI, contractAddress } from '../contractConfig';
import '../tokenDetails.css';
import { QRCodeCanvas } from 'qrcode.react';
import RecordEvent from './RecordEvent';

const TokenDetail = ({ mode = 'token' }) => {
  const { tokenId } = useParams();

  // --- Data state
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Verify state
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  // --- UI toggles
  const [showTrackGroup, setShowTrackGroup] = useState(false); // top-level "Track…" collapsible
  const [showTokenDetails, setShowTokenDetails] = useState(mode === 'token');
  const [showEvents, setShowEvents] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showRecordEvent, setShowRecordEvent] = useState(false); // top-level sibling

  // Reset toggles if mode changes
  useEffect(() => {
    setShowTokenDetails(mode === 'token');
    setShowEvents(false);
    setShowQR(false);
    setShowRecordEvent(false);
    setShowTrackGroup(false);
  }, [mode]);

  // --- Web3 (memoized)
  const infuraKey = process.env.REACT_APP_INFURA_API_KEY;
  const web3 = useMemo(() => {
    const rpc = infuraKey
      ? `https://polygon-amoy.infura.io/v3/${infuraKey}`
      : 'https://rpc-amoy.polygon.technology';
    return new Web3(rpc);
  }, [infuraKey]);

  // --- Fetch token (backend)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const { data } = await axios.get(`${apiUrl}/api/tokens/${tokenId}`);
        if (!cancelled) setToken(data);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to fetch token details');
          console.error('Error fetching token details:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tokenId]);

  // --- Chain helpers
  const fetchTokenFromBlockchain = async (id) => {
    try {
      const contract = new web3.eth.Contract(contractABI, contractAddress);
      return await contract.methods.getTokenEvents(id).call();
    } catch (err) {
      console.error(`Error fetching token events for tokenId ${id}:`, err);
      return null;
    }
  };

  const verifyTokenData = (backendToken, blockchainEvents) => {
    try {
      if (!Array.isArray(blockchainEvents)) return false;
      return blockchainEvents.some(
        (e) => e?.metadataHash === backendToken?.metadataHash && e?.signature === backendToken?.signature
      );
    } catch (err) {
      console.error('Error verifying token data:', err);
      return false;
    }
  };

  const handleVerifyToken = async (t) => {
    setIsVerifying(true);
    try {
      const blockchainEvents = await fetchTokenFromBlockchain(t.tokenId);
      if (!blockchainEvents || blockchainEvents.length === 0) {
        setVerificationStatus('No on-chain events found for this token');
        return;
      }
      const isVerified = verifyTokenData(t, blockchainEvents);
      setVerificationStatus(isVerified ? 'Verified with Blockchain' : 'Mismatch with Blockchain');
    } catch (err) {
      console.error('Error verifying token:', err);
      setVerificationStatus('Error verifying token');
    } finally {
      setIsVerifying(false);
    }
  };

  // --- URLs (used by QR)
  const detailsUrl = `${window.location.origin}/tokens/${tokenId}`;
  const eventsUrl  = `${window.location.origin}/tokens/${tokenId}/events`;

  // If you created a public read-only page like /share/:tokenId, use it here. 
  // Otherwise we fall back to the events page, which always shows fresh data.
  const shareUrl   = detailsUrl || eventsUrl;

  // --- QR helpers
  const qrRef = useRef(null);
  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `ApToken_${tokenId}_QR.png`;
    a.click();
  };

  // --- Render helpers
  const renderTokenDetails = () => (
    <div className="token-details-box">
      <p><strong>Passport ID:</strong> {token.tokenId}</p>
      <p><strong>Passport Name:</strong> {token.name}</p>
      <p><strong>Passport Description:</strong> {token.description}</p>
      <p>
        <strong>Authorisation License Url:</strong>{' '}
        <a href={token.url} target="_blank" rel="noopener noreferrer">{token.url}</a>
      </p>
      <p><strong>Metadata Hash:</strong> {token.metadataHash}</p>
      <p><strong>Signature:</strong> {token.signature}</p>
      <p><strong>Transaction Hash:</strong> {token.transactionHash}</p>
      <p><strong>Timestamp:</strong> {new Date(token.timestamp).toLocaleString()}</p>

      <button onClick={() => handleVerifyToken(token)} disabled={isVerifying}>
        {isVerifying ? 'Verifying...' : 'Verify with Blockchain'}
      </button>

      {verificationStatus && (
        <p style={{ color: verificationStatus.startsWith('Verified') ? 'green' : 'red', marginTop: 8 }}>
          {verificationStatus}
        </p>
      )}
    </div>
  );

  const renderEvents = () => {
    const list = Array.isArray(token?.events) ? token.events : [];
    if (!list.length) return <p>No events found for this token.</p>;
    return (
      <div className="provenance-events-box">
        {list.map((event, i) => (
          <div key={i} className="event-item">
            <h4>Event {i + 1}: {event.eventName}</h4>
            <p><strong>Description:</strong> {event.eventDescription}</p>
            <p><strong>Location:</strong> {event.eventAddress}</p>
            <p><strong>Event Hash:</strong> {event.eventHash}</p>
            <p><strong>Signature:</strong> {event.signature}</p>
            <p>
              <strong>File:</strong>{' '}
              <a href={event.file} target="_blank" rel="noopener noreferrer">{event.file}</a>
            </p>
            <p><strong>Transaction Hash:</strong> {event.transactionHash}</p>
            <p><strong>Timestamp:</strong> {new Date(event.timestamp).toLocaleString()}</p>
          </div>
        ))}
      </div>
    );
  };

  // --- Early exits
  if (loading) return <p>Loading product passport details...</p>;
  if (error)   return <p style={{ color: 'red' }}>⚠ {error}</p>;
  if (!token)  return null;

  return (
    <div className="container">
      {mode === 'event' && (
        <>
          {/* Top-level: Track group */}
          <h3>
            <button
              type="button"
              aria-expanded={showTrackGroup}
              aria-controls="track-group"
              onClick={() => setShowTrackGroup(v => !v)}
              className="toggle-btn"
           >
              {showTrackGroup ? '[-]' : '[+]'} Track Provenance by Event
            </button>
          </h3>

          {showTrackGroup && (
            <div className="track-inner">
              {/* 1) View ApToken Details */}
              <h2>
                <button
                  type="button"
                  aria-expanded={showTokenDetails}
                  aria-controls="token-details"
                  onClick={() => setShowTokenDetails(v => !v)}
                  style={{ cursor: 'pointer' }}
              >
                  {showTokenDetails ? '[-]' : '[+]'} ApToken #{token.tokenId} Details
                </button>
              </h2>
              {showTokenDetails && renderTokenDetails()}

              {/* 2) Provenance Events */}
              <h3>
                <button
                  type="button"
                  aria-expanded={showEvents}
                  aria-controls="prov-events"
                  onClick={() => setShowEvents(v => !v)}
                  style={{ cursor: 'pointer' }}
              >
                  {showEvents ? '[-]' : '[+]'} Provenance Events
                </button>
              </h3>
              {showEvents && renderEvents()}
            </div>
          )}

          {/* Top-level: Record New Provenance */}
          <h3>
            <button
              type="button"
              aria-expanded={showRecordEvent}
              aria-controls="record-event"
              onClick={() => setShowRecordEvent(v => !v)}
              className="toggle-btn"
          >
              {showRecordEvent ? '[-]' : '[+]'} Record New Provenance by Event
            </button>
          </h3>
          {showRecordEvent && (
            <div className="record-event-box">
              <RecordEvent tokenId={tokenId} hideTokenId />
            </div>
          )}

          {/* Top-level:Generate Provenance QR Code */}
              <h3>
                <button
                  type="button"
                  aria-expanded={showQR}
                  aria-controls="qr-section"
                  onClick={() => setShowQR(v => !v)}
                  className="toggle-btn"
              >
                {showQR ? '[-]' : '[+]'} Generate Provenance QR Code
                </button>
              </h3>
              {showQR && (
                <div ref={qrRef} className="track-subcontent" style={{ marginTop: 10 }}>
                  <QRCodeCanvas value={shareUrl} size={160} />
                  <p>Scan to view full passport details & provenance history (always up to date).</p>
                  <button onClick={downloadQR}>Download QR (PNG)</button>
                </div>
              )}
        </>
      )}

      {mode === 'token' && (
        <>
          <h3>
            <button
              type="button"
              onClick={() => setShowTokenDetails(v => !v)}
              className="toggle-btn"
              aria-expanded={showTokenDetails}
              aria-controls="token-only-details"
            >
              {showTokenDetails ? '[-]' : '[+]'} View Product Passport Details
            </button>
          </h3>
          {showTokenDetails && (
            <div id="token-only-details" className="track-subcontent">
              {renderTokenDetails()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TokenDetail;